import { InputAdornment } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { FormTextField } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

const MAX_FILE_NAME_LENGTH = 255;

enum AddressFileRenameFormField {
  Name = 'name',
}

interface AddressFileRenameFormValues {
  [AddressFileRenameFormField.Name]: string;
}

function splitFileName(fileName: string) {
  const dotIndex = fileName.lastIndexOf('.');

  return dotIndex === -1
    ? { baseName: fileName, extension: '' }
    : { baseName: fileName.slice(0, dotIndex), extension: fileName.slice(dotIndex) };
}

export const AddressFileRenameModal = () => {
  const { closeModal } = useModalMethods(Modals.AddressFileRename);
  const { modalProps } = useModalState(Modals.AddressFileRename);

  const fileName = modalProps?.fileName ?? '';
  const { baseName, extension } = splitFileName(fileName);

  const formMethods = useForm<AddressFileRenameFormValues>({
    defaultValues: { [AddressFileRenameFormField.Name]: baseName },
  });
  const name = useWatch({
    control: formMethods.control,
    name: AddressFileRenameFormField.Name,
  });

  const trimmedName = name.trim();
  const canRename =
    fileName !== '' && trimmedName !== '' && trimmedName + extension !== fileName;

  const handleRename = () => {
    const newName = formMethods.getValues(AddressFileRenameFormField.Name).trim();
    modalProps?.onRename?.(newName + extension);
    closeModal();
  };

  return (
    <ModalContainer
      open
      size="small"
      title="Rename file"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'address-file-rename-cancel' },
        {
          label: 'Rename file',
          kind: 'primary',
          onClick: handleRename,
          disabled: !canRename,
          testId: 'address-file-rename-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="address-file-rename-modal"
    >
      <GenericForm<AddressFileRenameFormValues> formMethods={formMethods}>
        <FormTextField
          type="textfield"
          name={AddressFileRenameFormField.Name}
          label="File name"
          textFieldProps={{
            inputProps: {
              maxLength: MAX_FILE_NAME_LENGTH,
              'data-testid': 'address-file-rename-input',
            },
            InputProps: {
              endAdornment: extension ? (
                <InputAdornment position="end">{extension}</InputAdornment>
              ) : undefined,
            },
          }}
        />
      </GenericForm>
    </ModalContainer>
  );
};
